/**
 * Закрытый набор кодов возражений клиента (`objection_codes_v2`).
 *
 * Зачем: свободная метка `objections[].type` на потоке не сходится — на проде
 * 3 801 возражение за 30 дней названо 2 537 разными метками, и приведение
 * регистра не сводит ни одной («ціна» / «price» / «бюджет» различаются смыслом
 * записи, а не написанием). Код выбирает модель, метка остаётся формулировкой
 * для человека. Спека: docs/plans/2026-09-18-voc-canonical-labels.md, §3.1.
 *
 * Набор платформенный и версионируемый: сравнение между проектами требует
 * одного словаря. Порядок значим только для `other` — он последний.
 *
 * Файл намеренно без импортов: его тянет `schemas/prompt-builder.ts`, а кольцо
 * импортов core ↔ api-types ломается от любого лишнего ребра (#839). Он же —
 * листовой вход `@aica/core/voc/objection-codes`, которым api-types берёт набор
 * значением (`schemas/sales-methodology.ts`, #766): баррель `@aica/core/voc`
 * для этого не годится, он тянет `schemas/analysis-client-config`, а тот
 * value-импортирует api-types.
 */

/**
 * Версия СМЫСЛА кодов, а не только их списка: определения
 * (`OBJECTION_CODE_DEFINITIONS`) решают, куда модель кладёт возражение, и
 * их правка переносит возражения между кодами. Код, записанный под одним
 * смыслом, не должен складываться с кодами другого — поэтому любая правка
 * определений поднимает версию (растяжка — тест `voc.test.ts`).
 *
 * v1 — определения #882; не выпущена тегом и не породила ни одного анализа.
 * v2 — ревизия #898: срочность — delivery_availability, а не timing; отказ от
 * предложенного бренда/модели/б/у — product_fit; замена вместо ремонта —
 * returns_compensation; сборка — responsibility_dispute.
 */
export const OBJECTION_CODES_VERSION = 'objection_codes_v2';

export const OBJECTION_CODES = [
  // Продажи
  'price',
  'budget',
  'financing',
  'payment_terms',
  'no_need',
  'timing',
  'delivery_availability',
  'product_fit',
  'quality_reliability',
  'trust_service',
  'competitor_alternative',
  'decision_authority',
  'indecision',
  'documents_contract',
  'upsell_declined',
  // Сервис: 17,3 % возражений приходят из support / complaint / billing /
  // cancellation — без этих кодов они ушли бы в `other` и испортили бы меру
  // годности набора.
  'returns_compensation',
  'responsibility_dispute',
  'unwanted_contact',
  'other',
] as const;

export type ObjectionCode = (typeof OBJECTION_CODES)[number];

/**
 * Признак чтения «код не удалось определить». В набор не входит намеренно:
 * модель его вернуть не может, поэтому доля `unmapped` измеряет покрытие
 * истории, а доля `other` — годность набора. Смешать их — потерять обе меры.
 */
export const UNMAPPED_OBJECTION_CODE = 'unmapped';

/**
 * `signal_kind` строк возражений в `voc_label_map`. Одна константа на
 * писателя (скрипт наполнения, ops-tools) и читателя (агрегат карточки):
 * разойдись они строкой — читатель молча не нашёл бы ни одной строки.
 */
export const OBJECTION_SIGNAL_KIND = 'objection';

const OBJECTION_CODE_SET: ReadonlySet<string> = new Set<string>(OBJECTION_CODES);

export function isObjectionCode(value: unknown): value is ObjectionCode {
  return typeof value === 'string' && OBJECTION_CODE_SET.has(value);
}

/**
 * Второй код годен, только если он из набора и не повторяет первый; иначе null.
 * Одно определение на запись (финализатор) и чтение (`resolveObjectionCode`):
 * две копии правила разошлись бы при первом же его изменении.
 */
export function resolveSecondaryObjectionCode(
  value: unknown,
  primary: ObjectionCode
): ObjectionCode | null {
  return isObjectionCode(value) && value !== primary ? value : null;
}
